<template>
  <v-card outlined class="block">
    <h3>{{ $t('hedgeOS.dashboard.hedgeBook.title') }} <span class="meta">{{ $t('hedgeOS.dashboard.hedgeBook.subtitle') }}</span></h3>
    <table class="mtm-table">
      <thead>
        <tr>
          <th>{{ $t('hedgeOS.dashboard.hedgeBook.headers.instrument') }}</th>
          <th>{{ $t('hedgeOS.dashboard.hedgeBook.headers.linkedOp') }}</th>
          <th style="text-align:right;">{{ $t('hedgeOS.dashboard.hedgeBook.headers.strikeFwd') }}</th>
          <th style="text-align:right;">{{ $t('hedgeOS.dashboard.hedgeBook.headers.mtm') }}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in instruments" :key="row.linkedOp + row.label">
          <td class="sym">
            {{ row.label }} <span class="tag" :class="row.side">{{ sideLabel(row.side) }}</span><br>
            <span style="color:var(--muted);font-size:0.65rem;">{{ $t(`hedgeOS.dashboard.hedgeBook.notes.${row.noteKey}`) }}</span>
          </td>
          <td style="font-size:0.65rem;color:var(--dim);">{{ row.linkedOp }}</td>
          <td style="text-align:right;">{{ row.strike }}</td>
          <td style="text-align:right;" :class="row.mtm.startsWith('-') ? 'neg' : 'pos'">{{ row.mtm }}</td>
          <td>
            <svg width="60" height="24" viewBox="0 0 60 24">
              <path class="spark" :stroke="row.sparkColor" :d="row.sparkPath" />
            </svg>
          </td>
        </tr>
      </tbody>
    </table>
  </v-card>
</template>

<script>
export default {
  name: "HedgeBook",
  data() {
    return {
      instruments: [
        { label: "NDF USD 5M", side: "buy", noteKey: "ndfUsd5m", linkedOp: "FIN-0017", strike: "5.7120", mtm: "+ 611,000", sparkColor: "#7bc4a4", sparkPath: "M0,18 L10,16 L20,14 L30,9 L40,8 L50,6 L60,4" },
        { label: "DOL·Q26", side: "buy", noteKey: "dolQ26", linkedOp: "FIN-0017", strike: "5.7450", mtm: "+ 1,173,000", sparkColor: "#7bc4a4", sparkPath: "M0,20 L10,18 L20,14 L30,12 L40,10 L50,6 L60,3" },
        { label: "NDF USD 0.7M", side: "buy", noteKey: "ndfUsd07mImp89", linkedOp: "IMP-0089", strike: "5.7120", mtm: "+ 88,000", sparkColor: "#7bc4a4", sparkPath: "M0,16 L10,14 L20,12 L30,10 L40,9 L50,7 L60,5" },
        { label: "LME AL · 3M", side: "buy", noteKey: "lmeAl3mImp89", linkedOp: "IMP-0089", strike: "2,380 USD/t", mtm: "+ 130,000", sparkColor: "#7bc4a4", sparkPath: "M0,18 L10,16 L20,13 L30,11 L40,10 L50,8 L60,6" },
        { label: "NDF EUR 2.8M", side: "buy", noteKey: "ndfEur28m", linkedOp: "IMP-0091", strike: "6.3125", mtm: "+ 86,000", sparkColor: "#7bc4a4", sparkPath: "M0,14 L10,15 L20,12 L30,13 L40,10 L50,11 L60,9" },
        { label: "CBOT SOY · ago", side: "sell", noteKey: "cbotSoyAug", linkedOp: "EXP-0212", strike: "1,062 c/bu", mtm: "+ 282,000", sparkColor: "#7bc4a4", sparkPath: "M0,8 L10,10 L20,8 L30,6 L40,8 L50,5 L60,4" },
        { label: "NDF USD 1.3M", side: "sell", noteKey: "ndfUsd13m", linkedOp: "EXP-0212", strike: "5.8540", mtm: "+ 130,000", sparkColor: "#7bc4a4", sparkPath: "M0,12 L10,11 L20,9 L30,8 L40,6 L50,5 L60,3" },
        { label: "NDF USD 4M", side: "buy", noteKey: "ndfUsd4m", linkedOp: "EXP-0205", strike: "5.8410", mtm: "- 28,400", sparkColor: "#ff4d1f", sparkPath: "M0,8 L10,10 L20,9 L30,14 L40,15 L50,16 L60,18" },
      ],
    };
  },
  methods: {
    sideLabel(side) {
      return side === "sell" ? this.$t("hedgeOS.dashboard.sideSell") : this.$t("hedgeOS.dashboard.sideBuy");
    },
  },
};
</script>

<style scoped>
.block {
  padding: 2rem;
}

.block h3 {
  font-family: 'Fraunces', serif;
  font-weight: 600;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin-bottom: 1.5rem;
  padding-bottom: 0.8rem;
  border-bottom: 1px solid var(--line);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.block h3 .meta {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.65rem;
  color: var(--muted);
  font-weight: 400;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.mtm-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.75rem;
}

.mtm-table th {
  text-align: left;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.62rem;
  font-weight: 400;
  padding: 0.6rem 0.5rem;
  border-bottom: 1px solid var(--line);
}

.mtm-table td {
  padding: 0.8rem 0.5rem;
  border-bottom: 1px solid var(--line);
}

.mtm-table tr:last-child td {
  border-bottom: none;
}

.mtm-table tr:hover td {
  background: var(--surface-2);
}

.mtm-table .sym {
  font-weight: 600;
  color: var(--paper);
}

.tag {
  display: inline-block;
  padding: 2px 6px;
  font-size: 0.6rem;
  margin: 0 .5rem;
  border: 1px solid var(--line);
  color: var(--dim);
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.tag.buy {
  border-color: var(--cool);
  color: var(--cool);
}

.tag.sell {
  border-color: var(--hot);
  color: var(--hot);
}

.pos {
  color: var(--cool);
}

.neg {
  color: var(--hot);
}

.spark {
  stroke-width: 1.5;
  fill: none;
}
</style>
